/** TanStack Query hooks over the CIGS API. One query key namespace: ['cigs', ...]. */
import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import { apiFetch, newCorrelationId } from './client';
import { endpoints } from './endpoints';
import {
  normalizeDepartment,
  normalizeDetail,
  normalizeHistory,
  normalizeList,
  normalizeMe,
  normalizeSchema,
  normalizeView,
} from './normalize';
import type {
  AttrCell,
  ChangeLogEntry,
  DepartmentTab,
  EntityDetail,
  EntityListResult,
  Me,
  SchemaDto,
  ViewDef,
} from './types';

export interface ColumnFilter {
  attrCode: string;
  /** option codes / ids for selects, [min,max] for numbers, string for text, boolean for flags */
  value: unknown;
}

export interface RegistryQuery {
  entityType: string;
  /** entity id of the department; the registry filters on the IN_SCOPE_FOR edge */
  departmentId?: string | null;
  search?: string;
  filters?: ColumnFilter[];
  sort?: Array<{ id: string; desc: boolean }>;
  hotListOnly?: boolean;
  page?: number;
  pageSize?: number;
}

/**
 * L2's list contract (api/src/registry/filter-model.ts): `type`, `q`, repeatable
 * `filter=<field>:<op>:<value>` where field is `attr:<code>`, `edge:<rel_code>`, `key`, `name`
 * or `description`, repeatable `sort=<field>:<dir>`, plus `page`/`pageSize` (max 500).
 */
export function registrySearchParams(q: RegistryQuery): string {
  const p = new URLSearchParams();
  p.set('type', q.entityType);
  if (q.search) p.set('q', q.search);
  if (q.departmentId) p.append('filter', `edge:IN_SCOPE_FOR:eq:${q.departmentId}`);
  if (q.hotListOnly) p.append('filter', 'attr:hot_list:eq:true');
  for (const f of q.filters ?? []) {
    if (Array.isArray(f.value)) p.append('filter', `attr:${f.attrCode}:in:${f.value.join(',')}`);
    else if (typeof f.value === 'boolean') p.append('filter', `attr:${f.attrCode}:eq:${f.value}`);
    else if (f.value !== null && f.value !== undefined) p.append('filter', `attr:${f.attrCode}:contains:${String(f.value)}`);
  }
  for (const s of q.sort ?? []) p.append('sort', `${s.id}:${s.desc ? 'desc' : 'asc'}`);
  p.set('page', String(q.page ?? 1));
  p.set('pageSize', String(Math.min(q.pageSize ?? 500, 500)));
  return p.toString();
}

export const qk = {
  me: ['cigs', 'me'] as const,
  schema: (t: string) => ['cigs', 'schema', t] as const,
  departments: ['cigs', 'departments'] as const,
  views: (t: string) => ['cigs', 'views', t] as const,
  registry: (q: RegistryQuery) => ['cigs', 'registry', q] as const,
  entity: (id: string) => ['cigs', 'entity', id] as const,
  history: (id: string) => ['cigs', 'history', id] as const,
};

export function useMe(enabled = true) {
  return useQuery<Me>({
    queryKey: qk.me,
    enabled,
    retry: false,
    queryFn: async () => normalizeMe(await apiFetch<Record<string, unknown>>(endpoints.me)),
  });
}

export function useSchema(entityType: string, options?: Partial<UseQueryOptions<SchemaDto>>) {
  return useQuery<SchemaDto>({
    queryKey: qk.schema(entityType),
    staleTime: 5 * 60_000,
    queryFn: async () => normalizeSchema(await apiFetch<Record<string, unknown>>(endpoints.schema), entityType),
    ...options,
  });
}

/**
 * Departments are entities, so they come from the same list route. Who may edit which
 * department is decided from the caller's DeptEditor scopes — and re-checked by the server on
 * every write.
 */
export function useDepartments(me?: Me) {
  return useQuery<DepartmentTab[]>({
    queryKey: [...qk.departments, me?.id ?? 'anon'],
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const raw = await apiFetch<never>(`${endpoints.entities}?${registrySearchParams({ entityType: 'department', pageSize: 200 })}`);
      const list = normalizeList(raw);
      const scopes = new Set((me?.roles ?? []).filter((r) => r.role === 'DeptEditor').map((r) => r.scopeEntityId));
      return list.rows
        .map((row) =>
          normalizeDepartment({
            id: row.id,
            key: row.key,
            name: row.name,
            canEdit: Boolean(me?.isHeadOfIT) || scopes.has(row.id),
          }),
        )
        .sort((a, b) => a.name.localeCompare(b.name));
    },
  });
}

export function useViews(entityType: string) {
  return useQuery<ViewDef[]>({
    queryKey: qk.views(entityType),
    staleTime: 60_000,
    queryFn: async () => {
      const raw = await apiFetch<unknown>(endpoints.views(entityType));
      const list = Array.isArray(raw) ? raw : ((raw as { rows?: unknown[]; views?: unknown[] }).rows ?? (raw as { views?: unknown[] }).views ?? []);
      return (list as Record<string, unknown>[]).map(normalizeView);
    },
  });
}

export function useRegistry(q: RegistryQuery, enabled = true) {
  return useQuery<EntityListResult>({
    queryKey: qk.registry(q),
    enabled,
    placeholderData: (prev) => prev,
    queryFn: async () => normalizeList(await apiFetch<never>(`${endpoints.entities}?${registrySearchParams(q)}`)),
  });
}

export function useEntity(id: string | null) {
  return useQuery<EntityDetail>({
    queryKey: qk.entity(id ?? 'none'),
    enabled: Boolean(id),
    queryFn: async () => normalizeDetail(await apiFetch<Record<string, unknown>>(endpoints.entity(id!))),
  });
}

export function useEntityHistory(id: string | null, enabled = true) {
  return useQuery<ChangeLogEntry[]>({
    queryKey: qk.history(id ?? 'none'),
    enabled: Boolean(id) && enabled,
    queryFn: async () => {
      const raw = await apiFetch<unknown>(endpoints.entityHistory(id!));
      const list = Array.isArray(raw)
        ? raw
        : ((raw as { entries?: unknown[]; rows?: unknown[] }).entries ?? (raw as { rows?: unknown[] }).rows ?? []);
      return (list as Record<string, unknown>[]).map(normalizeHistory);
    },
  });
}

export interface SetAttributeInput {
  entityId: string;
  attrCode: string;
  /** null clears the value (back to Unassessed) */
  value: string | number | boolean | string[] | null;
  optionId?: string | null;
  reason?: string;
}

/**
 * Optimistic attribute write. Patches every cached registry page and the open drawer, then
 * rolls back on failure — the server is the authority (it may refuse with 403).
 */
export function useSetAttribute() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: SetAttributeInput) =>
      apiFetch<unknown>(endpoints.entityAttribute(input.entityId, input.attrCode), {
        method: 'PATCH',
        correlationId: newCorrelationId(),
        body: { value: input.value, reason: input.reason },
      }),
    onMutate: async (input) => {
      await qc.cancelQueries({ queryKey: ['cigs', 'registry'] });
      await qc.cancelQueries({ queryKey: qk.entity(input.entityId) });
      const snapshot = qc.getQueriesData<EntityListResult>({ queryKey: ['cigs', 'registry'] });
      const detail = qc.getQueryData<EntityDetail>(qk.entity(input.entityId));
      const optimistic: AttrCell = {
        value: Array.isArray(input.value) ? null : input.value,
        optionId: input.optionId ?? null,
        optionCodes: Array.isArray(input.value) ? input.value : null,
        provenance: { source: 'ui', at: new Date().toISOString(), by: 'you' },
      };

      for (const [key, data] of snapshot) {
        if (!data) continue;
        qc.setQueryData(key, {
          ...data,
          rows: data.rows.map((r) => (r.id === input.entityId ? { ...r, attrs: { ...r.attrs, [input.attrCode]: optimistic } } : r)),
        });
      }
      if (detail) {
        qc.setQueryData(qk.entity(input.entityId), {
          ...detail,
          attrs: { ...detail.attrs, [input.attrCode]: optimistic },
        });
      }
      return { snapshot, detail };
    },
    onError: (_err, input, ctx) => {
      for (const [key, data] of ctx?.snapshot ?? []) qc.setQueryData(key, data);
      if (ctx?.detail) qc.setQueryData(qk.entity(input.entityId), ctx.detail);
    },
    onSettled: (_data, _err, input) => {
      void qc.invalidateQueries({ queryKey: ['cigs', 'registry'] });
      void qc.invalidateQueries({ queryKey: qk.entity(input.entityId) });
      void qc.invalidateQueries({ queryKey: qk.history(input.entityId) });
    },
  });
}

export interface SaveViewInput {
  id?: string;
  code: string;
  label: string;
  entityType: string;
  columns: ViewDef['columns'];
  filters?: Record<string, unknown>;
  sort?: Array<{ id: string; desc: boolean }>;
  isShared?: boolean;
  departmentKey?: string | null;
}

export function useSaveView() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: SaveViewInput) => {
      const body = {
        entityTypeCode: input.entityType,
        code: input.code,
        label: input.label,
        columns: input.columns.map((c) => ({ attr_code: c.attrCode, width: c.width, pinned: c.pinned, hidden: c.hidden })),
        filters: input.filters ?? {},
        sort: input.sort ?? [],
        isShared: input.isShared ?? false,
        reason: input.isShared ? `Shared registry view "${input.label}"` : undefined,
      };
      return input.id
        ? apiFetch<unknown>(endpoints.view(input.id), { method: 'PUT', body, correlationId: newCorrelationId() })
        : apiFetch<unknown>(endpoints.createView, { method: 'POST', body, correlationId: newCorrelationId() });
    },
    onSuccess: (_d, input) => {
      void qc.invalidateQueries({ queryKey: qk.views(input.entityType) });
    },
  });
}
